import React, { createContext, useContext, useState, useEffect } from "react";
import { IStaffRole } from "../types/admin.types";

interface RoleContextType {
  role: IStaffRole | null;
  isSuperAdmin: boolean;
  isRelationshipManager: boolean;
  roleDisplayName: string;
}

const RoleContext = createContext<RoleContextType>({
  role: null,
  isSuperAdmin: false,
  isRelationshipManager: false,
  roleDisplayName: "",
});

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<IStaffRole | null>(() => {
    try {
      const data = localStorage.getItem("adminData");
      if (!data) return null;
      const parsed = JSON.parse(data);
      // role is now a populated StaffRole object
      return parsed.role && typeof parsed.role === "object" ? (parsed.role as IStaffRole) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const handler = () => {
      try {
        const data = localStorage.getItem("adminData");
        if (!data) {
          setRole(null);
          return;
        }
        const parsed = JSON.parse(data);
        setRole(parsed.role && typeof parsed.role === "object" ? (parsed.role as IStaffRole) : null);
      } catch {
        setRole(null);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return (
    <RoleContext.Provider
      value={{
        role,
        isSuperAdmin: role?.name === "super_admin",
        isRelationshipManager: role?.name === "relationship_manager",
        roleDisplayName: role?.label ?? "",
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = (): RoleContextType => useContext(RoleContext);
